import type { FocusEvent, RefObject } from "react";
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { cn } from "@krea/shared/utils";
import { FormatNumber } from "@krea/shared/utils/formatNumber";

import { Error as FieldError } from "./fields/Error";
import { IntegerInput } from "./input/integer-input";
import { Label } from "./Label";
import { Slider } from "./Slider";
import { getClosestIndex } from "./slider-utils";

const sizeVariants = {
  sm: { input: "h-9 text-xs", append: "text-xs" },
  md: { input: "h-10 text-sm", append: "text-sm" },
  lg: { input: "h-12 text-base", append: "text-base" },
  xl: { input: "h-16 text-lg", append: "text-lg" },
};

export interface ICombinedInputSliderProps {
  value: number | null;
  valuesList: number[];
  onChange: (value: number) => void;
  label?: string;
  textAppend?: string;
  placeholder?: string;
  size?: "sm" | "md" | "lg" | "xl";
  error?: string;
  className?: string;
  trackClassName?: string;
  formatter?: (value: number) => string;
  name?: string;
  onBlur?: () => void;
  showMinMax?: boolean;
  dataTestId?: string;
  /** Input id; the label points at it. Defaults to a generated unique id. */
  id?: string;
  /**
   * Opt-in "hero" layout for the amount step: value becomes a large borderless
   * figure (still editable) with persistent min/max labels below the slider.
   * Default false — every other consumer keeps the standard bordered layout.
   */
  emphasizeValue?: boolean;
  /** Tailwind background class for the field, slider mask, and min/max chips. */
  bgColorClass?: string;
  /**
   * Portal target for the floating min/max chips. Defaults to `document.body`
   * to escape ancestor `overflow:hidden` / `transform` clipping. In scoped-CSS
   * embeds (e.g. the standalone widget where Tailwind utilities are prefixed
   * with `.ka3r`), pass the scoped root so the chips' classes still match.
   */
  portalContainer?: HTMLElement | null;
}

/**
 * Focus the input and place the caret at the end, triggered from the
 * slider's onPointerDown / onPointerUp.
 *
 * `preventScroll` should be true when the soft keyboard is already open
 * (keyboard was up before the drag started) to avoid a jarring scroll.
 * When false the browser naturally scrolls the field into the visible area
 * as the keyboard opens.
 *
 * Two-phase strategy:
 *  1. Synchronous focus() — mobile browsers only open the soft keyboard
 *     when focus() is called directly inside a user-gesture handler.
 *     Radix Slider will steal this back moments later when it focuses its
 *     own thumb, but the keyboard-open intent has already been registered.
 *  2. rAF focus() — after Radix has finished its synchronous pointer
 *     handling (and thumb focus), we steal focus back so the caret is
 *     visible. A second rAF then places the caret at end once
 *     useNumericInput has committed the formatted display value.
 */
function focusInputAtEnd(
  ref: RefObject<HTMLInputElement | null>,
  preventScroll: boolean,
): void {
  const el = ref.current;
  if (!el) return;

  // Phase 1: synchronous — opens soft keyboard on mobile.
  el.focus({ preventScroll });

  // Phase 2: deferred — reclaims focus from Radix's thumb on desktop (and mobile).
  requestAnimationFrame(() => {
    const input = ref.current;
    if (!input) return;
    input.focus({ preventScroll });
    requestAnimationFrame(() => {
      if (!input.isConnected) return;
      const len = input.value.length;
      try {
        input.setSelectionRange(len, len);
      } catch {
        // ignore invalid selection
      }
    });
  });
}

/**
 * Find the containing block for a `position: absolute` descendant of `el` — the
 * nearest ancestor that establishes one (positioned, or with a transform /
 * filter / perspective / contain / will-change that creates a containing block).
 * Returns `null` when there is none, meaning the initial containing block (the
 * document) applies and document coordinates should be used.
 *
 * The min/max chips are portaled into `portalContainer` (e.g. the scoped `.ka3r`
 * widget root, which is itself `position: static`), so their real positioning
 * context is whatever containing block sits above them — the document on most
 * pages, but a positioned/transformed wrapper on some third-party embeds. We
 * resolve it at runtime instead of assuming it's the portal target.
 */
function getAbsoluteContainingBlock(el: HTMLElement): HTMLElement | null {
  let node = el.parentElement;
  while (node) {
    const cs = getComputedStyle(node);
    const contain = cs.contain;
    // willChange can be a comma-separated list, e.g. "transform, opacity".
    const willChange = cs.willChange.split(",").map((token) => token.trim());
    if (
      cs.position !== "static" ||
      cs.transform !== "none" ||
      cs.perspective !== "none" ||
      (cs.filter !== "none" && cs.filter !== "") ||
      willChange.includes("transform") ||
      willChange.includes("perspective") ||
      willChange.includes("filter") ||
      contain === "strict" ||
      contain === "content" ||
      contain.includes("layout") ||
      contain.includes("paint")
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

// Field/input/append/slider classes for the standard bordered layout.
const DEFAULT_INPUT_CLASS =
  "flex w-full appearance-none items-center border-0 bg-transparent mt-1 px-4 py-0 font-sans text-ellipsis text-base-strong shadow-none outline-hidden leading-6 focus-visible:border-0 hover:border-0";
const DEFAULT_APPEND_CLASS =
  "flex shrink-0 select-none items-center justify-center whitespace-nowrap pr-4 text-base-strong leading-6";

// Emphasized ("hero") layout: large borderless figure, still editable. text-4xl
// (36px token) is the closest fit — no preset sits between text-2xl and the
// website-only display-* classes. `field-sizing-content` hugs the value so it +
// unit read as one figure. Only the value changes; the slider keeps its default.
const EMPHASIZED_INPUT_CLASS =
  "w-auto min-w-0 max-w-full appearance-none border-0 bg-transparent px-0 py-0 text-left font-sans text-4xl font-bold tracking-tight text-black shadow-none outline-hidden leading-none field-sizing-content placeholder:font-normal placeholder:text-gray-500 focus-visible:border-0 hover:border-0";
const EMPHASIZED_APPEND_CLASS =
  "flex shrink-0 select-none items-baseline whitespace-nowrap pl-2 font-sans text-2xl font-semibold text-black leading-none";

interface IFieldLayout {
  field: string;
  inputWrap: string;
  input: string;
  append: string;
  sliderWrap: string;
  sliderInner: string | undefined;
}

/**
 * Per-element classes for both layouts in one place. The default branch is
 * equivalent to the pre-`emphasizeValue` markup, so consumers are unchanged.
 */
function resolveFieldLayout(
  emphasizeValue: boolean,
  ctx: {
    bgColorClass: string;
    borderClasses: Record<string, boolean>;
    inputSizeClass: string;
    appendSizeClass: string;
    focused: boolean;
  },
): IFieldLayout {
  if (emphasizeValue) {
    return {
      // Editable figure that hugs its content, left-aligned. The bottom border
      // under the value turns primary on focus.
      field: cn(
        "w-fit items-baseline border-b-2 pb-1 transition-colors duration-200",
        ctx.focused ? "border-primary" : "border-gray-300",
      ),
      inputWrap: "min-w-0",
      input: EMPHASIZED_INPUT_CLASS,
      append: EMPHASIZED_APPEND_CLASS,
      sliderWrap: "mt-8 px-1",
      sliderInner: undefined,
    };
  }
  return {
    field: cn(
      "items-center rounded-sm border border-solid transition-[color,box-shadow] duration-300 ease-out",
      ctx.bgColorClass,
      ctx.borderClasses,
    ),
    inputWrap: "min-w-0 flex-1 pb-2",
    input: cn(DEFAULT_INPUT_CLASS, ctx.inputSizeClass),
    append: cn(DEFAULT_APPEND_CLASS, ctx.appendSizeClass),
    sliderWrap: "absolute -bottom-1.5 left-0 right-0 mx-3 px-1",
    sliderInner: "relative z-1",
  };
}

/**
 * `bg`-colored backdrop behind the slider (top half masked) so the track looks
 * like it emerges from the bordered field. Renders nothing in emphasized mode
 * (no bordered box, so it'd be a stray rectangle).
 */
function renderSliderBackdrop(emphasizeValue: boolean, bgColorClass: string) {
  if (emphasizeValue) return null;
  return (
    <div
      aria-hidden
      className={cn(
        "pointer-events-none absolute inset-0 z-0",
        bgColorClass,
        "mask-[linear-gradient(to_bottom,black_0%,black_58%,transparent_58%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_bottom,black_0%,black_58%,transparent_58%,transparent_100%)]",
      )}
    />
  );
}

/**
 * Persistent min/max labels below the slider in gray-600 (via `text-helper`),
 * used only by emphasized mode in place of the floating drag chips.
 */
function renderEmphasizedMinMax(
  emphasizeValue: boolean,
  showMinMax: boolean,
  valuesList: number[],
  formatter: (value: number) => string,
  textAppend: string | undefined,
) {
  if (!emphasizeValue || !showMinMax || valuesList.length === 0) return null;
  const suffix = textAppend ? ` ${textAppend}` : "";
  return (
    <div className="mt-6 flex items-center justify-between text-helper">
      <span>
        {formatter(valuesList[0] as number)}
        {suffix}
      </span>
      <span>
        {formatter(valuesList[valuesList.length - 1] as number)}
        {suffix}
      </span>
    </div>
  );
}

const CombinedInputSlider = ({
  value,
  valuesList,
  onChange,
  label,
  textAppend,
  placeholder,
  size = "lg",
  error,
  className,
  trackClassName,
  formatter = FormatNumber,
  name,
  onBlur,
  showMinMax = true,
  dataTestId,
  id,
  emphasizeValue = false,
  bgColorClass = "bg-white",
  portalContainer,
}: ICombinedInputSliderProps) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const labelId = `${inputId}-label`;
  const inputRef = useRef<HTMLInputElement>(null);
  const sliderContainerRef = useRef<HTMLDivElement>(null);
  const minMaxChipsRef = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [sliderHover, setSliderHover] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [labelsPos, setLabelsPos] = useState<{
    left: number;
    width: number;
    top: number;
  } | null>(null);
  // Sync flag so blur/change handlers know whether a drag is in progress.
  const draggingRef = useRef(false);
  // True while the soft keyboard is visible; updated via input focus/blur events.
  const keyboardOpenRef = useRef(false);
  // Snapshot of keyboardOpenRef taken at pointerDown, before el.focus() fires
  // onFocus and sets keyboardOpenRef to true. Determines preventScroll for the
  // whole drag: false = first touch, let browser scroll; true = already open.
  const keyboardWasOpenAtDragStartRef = useRef(false);
  const { currentIndex, maxIndex } = useMemo(() => {
    const last = valuesList.length - 1;
    return {
      currentIndex: getClosestIndex(valuesList, value ?? 0),
      maxIndex: last >= 0 ? last : 0,
    };
  }, [valuesList, value]);

  const handleSliderChange = useCallback(
    ([index]: number[]) => {
      if (index === undefined) return;
      const newValue = valuesList[index];
      if (newValue !== undefined) onChange(newValue);
    },
    [onChange, valuesList],
  );

  const handleInputChange = useCallback(
    (v: number | null) => onChange(v ?? valuesList[0] ?? 0),
    [onChange, valuesList],
  );

  const handleInputFocus = useCallback(() => {
    setFocused(true);
    keyboardOpenRef.current = true;
  }, []);

  const handleInputBlur = useCallback(
    (_v: number | null, _e: FocusEvent<HTMLInputElement>) => {
      // Thumb takes focus during a drag — not a real blur.
      if (draggingRef.current) {
        // Only refocus (keep keyboard up) if it was open before this drag.
        if (keyboardWasOpenAtDragStartRef.current) {
          inputRef.current?.focus({ preventScroll: true });
        }
        return;
      }
      keyboardOpenRef.current = false;
      setFocused(false);
      onBlur?.();
    },
    [onBlur],
  );

  const handlePointerDown = useCallback(() => {
    // Snapshot before el.focus() fires onFocus and flips keyboardOpenRef.
    keyboardWasOpenAtDragStartRef.current = keyboardOpenRef.current;
    draggingRef.current = true;
    // If keyboard was already open, keep it open during the drag.
    // If not, don't open it yet — wait until pointerUp so it only opens
    // after the user finishes sliding, not while they're still dragging.
    if (keyboardWasOpenAtDragStartRef.current) {
      focusInputAtEnd(inputRef, true);
    }
    setIsDragging(true);
  }, []);

  const handlePointerUp = useCallback(() => {
    focusInputAtEnd(inputRef, keyboardWasOpenAtDragStartRef.current);
    // Defer so any pending blur events (from the thumb stealing focus on the
    // last drag frame) still see draggingRef=true and hit the early-return in
    // handleInputBlur — not the value-committing path.
    requestAnimationFrame(() => {
      draggingRef.current = false;
      setIsDragging(false);
    });
  }, []);

  const handlePointerLeave = useCallback(() => {
    requestAnimationFrame(() => {
      draggingRef.current = false;
      setIsDragging(false);
    });
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  const syncPosition = useCallback(() => {
    const el = sliderContainerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // Match the prior layout: left-3 / right-3 (0.75rem ≈ 12px) and
    // top-[calc(100%+1.25rem)] (1.25rem ≈ 20px below the wrapper).
    //
    // The chips use position:absolute, so their coordinates must be expressed
    // relative to their containing block. They're portaled into `portalContainer`
    // (a static `.ka3r` root in embeds) so the containing block is whatever
    // positioned/transformed ancestor sits above them — resolved at runtime:
    //   • none (initial containing block) → document coordinates (rect + scroll).
    //     Also avoids the iOS Safari position:fixed bug where the layout viewport
    //     and getBoundingClientRect()'s visual-viewport coords diverge by scrollY
    //     while the keyboard is open (we never use position:fixed).
    //   • a containing block → coordinates relative to its padding box, so the
    //     chips land under the slider regardless of the host page's layout.
    const cb = minMaxChipsRef.current
      ? getAbsoluteContainingBlock(minMaxChipsRef.current)
      : null;
    setLabelsPos((prev) => {
      let next: { left: number; width: number; top: number };
      if (cb) {
        const cbRect = cb.getBoundingClientRect();
        const cs = getComputedStyle(cb);
        const borderLeft = Number.parseFloat(cs.borderLeftWidth) || 0;
        const borderTop = Number.parseFloat(cs.borderTopWidth) || 0;
        next = {
          left: rect.left - cbRect.left - borderLeft + cb.scrollLeft + 12,
          width: rect.width - 24,
          top: rect.bottom - cbRect.top - borderTop + cb.scrollTop + 20,
        };
      } else {
        next = {
          left: rect.left + window.scrollX + 12,
          width: rect.width - 24,
          top: rect.bottom + window.scrollY + 20,
        };
      }
      if (
        prev &&
        prev.left === next.left &&
        prev.width === next.width &&
        prev.top === next.top
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  useLayoutEffect(() => {
    syncPosition();
    if (!isDragging) return;
    let raf: number;
    const tick = () => {
      syncPosition();
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isDragging, syncPosition]);

  // Recompute on viewport resize. Without this, the portaled chips keep
  // coordinates from the previous layout and, on a narrower viewport, their
  // right edge sticks out past the document width — adding horizontal scroll
  // (invisible, since they're opacity-0 while idle).
  useEffect(() => {
    window.addEventListener("resize", syncPosition);
    return () => window.removeEventListener("resize", syncPosition);
  }, [syncPosition]);

  // The first measurement can land mid-way through an ancestor's entry
  // animation (e.g. the form layout's `animate-step-slide-in` translateX), which
  // leaves the idle chips offset by the transform and, near the viewport edge,
  // overflowing it. A transform never fires resize (nor a ResizeObserver), so
  // resync once it settles. animationend bubbles up from the animated element,
  // so it can only be heard at the document; the containment check skips every
  // animation that is not on one of this slider's ancestors.
  useEffect(() => {
    const onAnimationEnd = (event: AnimationEvent) => {
      const container = sliderContainerRef.current;
      if (
        container &&
        event.target instanceof Node &&
        event.target.contains(container)
      ) {
        syncPosition();
      }
    };
    document.addEventListener("animationend", onAnimationEnd);
    return () => document.removeEventListener("animationend", onAnimationEnd);
  }, [syncPosition]);

  // Callback ref for the portaled chips. The first syncPosition() runs before
  // the portal exists, so it falls back to document coordinates; resync as soon
  // as the node mounts so the containing-block-relative math applies on the
  // first render (otherwise idle, off-screen chips could re-introduce overflow
  // in embeds until the first drag/resize). Stable identity → fires only on
  // mount/unmount, not every render.
  const setChipsRef = useCallback(
    (node: HTMLDivElement | null) => {
      minMaxChipsRef.current = node;
      if (node) syncPosition();
    },
    [syncPosition],
  );

  // The slider's value is an index into valuesList; announce the amount the
  // user actually sees (same formatter + unit as the min/max chips) instead.
  const displayedValue = value ?? valuesList[currentIndex];
  const valueText =
    displayedValue === undefined
      ? undefined
      : `${formatter(displayedValue)}${textAppend ? ` ${textAppend}` : ""}`;

  const variants = sizeVariants[size];
  const isActiveState = focused || isDragging;

  const borderClasses = {
    // primary-100 matches TextInput's resting border.
    "border-primary-100 hover:border-primary/30": !error && !isActiveState,
    "border-primary/30": sliderHover && !isActiveState,
    "border-primary": isActiveState && !error,
    "border-red-500": !!error,
  };

  const styles = resolveFieldLayout(emphasizeValue, {
    bgColorClass,
    borderClasses,
    inputSizeClass: variants.input,
    appendSizeClass: variants.append,
    focused,
  });

  return (
    <div
      className={cn(
        "flex flex-col overflow-visible",
        isDragging && "relative z-5",
        className,
      )}
    >
      {label && (
        <Label className="mb-2" htmlFor={inputId} id={labelId}>
          {label}
        </Label>
      )}

      <div ref={sliderContainerRef} className="relative overflow-visible">
        <div
          className={cn(
            "flex flex-nowrap font-sans font-semibold outline-hidden",
            styles.field,
          )}
        >
          <div className={styles.inputWrap}>
            <IntegerInput
              ref={inputRef}
              id={inputId}
              value={value}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onFocus={handleInputFocus}
              name={name}
              placeholder={placeholder}
              data-test-id={dataTestId}
              autoComplete="off"
              type="text"
              className={styles.input}
            />
          </div>

          {textAppend && (
            // Clicking the unit focuses the value — same affordance as
            // EmphasizedInput's and MoneyInput's suffix. preventDefault stops
            // mousedown from blurring the input first, which would commit and
            // reformat the value mid-edit. Stays a <div> (not <span>) so
            // styles.append keeps driving the flex layout in both branches.
            // biome-ignore lint/a11y/noStaticElementInteractions: pointer-only convenience — the input is the focus target and stays keyboard-accessible, so this must not be a tab stop or announce a role
            <div
              onMouseDown={(e) => {
                e.preventDefault();
                inputRef.current?.focus();
              }}
              className={cn(styles.append, "cursor-text")}
            >
              {textAppend}
            </div>
          )}
        </div>

        <div className={styles.sliderWrap}>
          {renderSliderBackdrop(emphasizeValue, bgColorClass)}
          <div className={styles.sliderInner}>
            <Slider
              trackClassName={cn(trackClassName, error && "bg-red-100")}
              onValueChange={handleSliderChange}
              value={[currentIndex]}
              step={1}
              min={0}
              max={maxIndex}
              onMouseEnter={() => setSliderHover(true)}
              onMouseLeave={() => setSliderHover(false)}
              onPointerDown={handlePointerDown}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerLeave}
              thumbProps={{
                ...(label
                  ? { "aria-labelledby": labelId }
                  : { "aria-label": placeholder }),
                "aria-valuetext": valueText,
              }}
            />
          </div>
        </div>

        {/*
         * Min / max — portaled to `portalContainer ?? <body>` with
         * position: absolute so they escape ancestor overflow:hidden clipping.
         * Coordinates are resolved against the chips' real containing block
         * (see syncPosition / getAbsoluteContainingBlock). Using absolute (not
         * fixed) avoids the iOS Safari issue where position:fixed is anchored to
         * the layout viewport while getBoundingClientRect() returns visual
         * viewport coords — the two diverge by scrollY when the keyboard is open.
         */}
        {/* Emphasized mode shows persistent min/max labels below the slider in
            gray-600, replacing the floating drag chips. */}
        {renderEmphasizedMinMax(
          emphasizeValue,
          showMinMax,
          valuesList,
          formatter,
          textAppend,
        )}

        {!emphasizeValue &&
          showMinMax &&
          valuesList.length > 0 &&
          mounted &&
          labelsPos &&
          createPortal(
            <div
              ref={setChipsRef}
              style={{
                position: "absolute",
                left: labelsPos.left,
                width: labelsPos.width,
                top: labelsPos.top,
                zIndex: 9999,
              }}
              className={cn(
                "pointer-events-none flex items-center justify-between gap-3 transition-opacity duration-200 ease-out",
                isDragging ? "opacity-100" : "opacity-0",
              )}
              aria-hidden={!isDragging}
            >
              <div className="rounded-md border border-gray-100 px-2.5 py-1 text-helper shadow-md bg-white">
                {formatter(valuesList[0] as number)}
                {textAppend ? ` ${textAppend}` : ""}
              </div>
              <div className="rounded-md border border-gray-100 px-2.5 py-1 text-helper shadow-md bg-white">
                {formatter(valuesList[valuesList.length - 1] as number)}
                {textAppend ? ` ${textAppend}` : ""}
              </div>
            </div>,
            portalContainer ?? document.body,
          )}
      </div>
      {error && <FieldError className="ml-1 mt-4">{error}</FieldError>}
    </div>
  );
};

export { CombinedInputSlider };
